import styled from "@emotion/styled";
import BorderLoader from "components/atoms/loader/BorderLoader";
import { i18nextT } from "hooks/i18n/i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { checkSendingFee } from "utility/purchase";

import type { TicketData } from "@spwn/types/firebase/database";
import type firebase from "firebase/app";
import type { ReactNode } from "react";

type GuideStatus = "idle" | "loading" | "invalid";

type CompletedFetch = { forUid: string; addressIsValid: boolean };
type LotteryCart = Record<string, number>;
type TicketMap = Record<string, TicketData>;

type UseEventTicketAddressEditGuideParams = {
  lotteryCart: LotteryCart;
  tickets: TicketMap;
  user: firebase.User;
};

type EventTicketAddressEditGuideGateProps = {
  lotteryCart: LotteryCart;
  tickets: TicketMap;
  user: firebase.User;
  contentWhenAddressValid: ReactNode;
};

const SettlementLayout = styled.div`
  @media screen and (min-width: 768px) {
    width: 740px;
    margin: 0 auto;
  }
`;

const Form01 = styled.div`
  margin-top: 40px;
  min-height: 120px;
  > p {
    margin-bottom: 15px;
  }
  > .guide_loader {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  > .not_registered {
    > p {
      font-size: 14px;
      line-height: 1.3em;
      letter-spacing: 1px;
      &:not(:last-child) {
        margin-bottom: 5px;
      }
      a {
        color: #11d4db;
        text-decoration: underline;
      }
    }
  }
`;

const EventTicketAddressEditGuideLoading: React.FC = () => (
  <SettlementLayout>
    <Form01>
      <div className="guide_loader">
        <BorderLoader />
      </div>
    </Form01>
  </SettlementLayout>
);

/** FIXME: ライブコマースの文言を持ってきているので個別で定義して利用する */
const EventTicketAddressEditGuideInvalid: React.FC = () => (
  <SettlementLayout>
    <Form01>
      <div className="not_registered">
        <p>{i18nextT("streaming.commerceCart.noticeInvalidAddress")}</p>
        <p>
          <Link to="/account/address" target="_blank" rel="noopener noreferrer">
            {i18nextT("streaming.commerceCart.editAddress")}
          </Link>
        </p>
      </div>
    </Form01>
  </SettlementLayout>
);

const isAddressRequiredTicket = (ticket?: TicketData) =>
  Boolean(ticket?.isAddressRequired) || Boolean(ticket?.isAddressRequiredNoFee);

/**
 * 抽選カートと user を入力に、住所ガイドの表示状態を返す
 *
 * 責務:
 * - 住所チェック対象かどうかを判定する
 * - checkSendingFee の結果を forUid 付きで保持し GuideStatus を決定する
 */
export const useEventTicketAddressEditGuide = ({
  lotteryCart,
  tickets,
  user,
}: UseEventTicketAddressEditGuideParams): GuideStatus => {
  const [completed, setCompleted] = useState<CompletedFetch | null>(null);

  const hasAddressRequiredTicket = useMemo(() => {
    return Object.keys(lotteryCart).some((ticketId) => {
      const count = Number(lotteryCart[ticketId] ?? 0);
      if (count <= 0) return false;
      // カートに実際に入っている住所必須チケットだけをチェック対象にする。
      return isAddressRequiredTicket(tickets[ticketId]);
    });
  }, [lotteryCart, tickets]);

  useEffect(() => {
    if (!hasAddressRequiredTicket) {
      setCompleted(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const sendingFee = await checkSendingFee(user);
        if (cancelled) return;
        // checkSendingFee が null を返す時点で、この画面では
        // 「応募を進められない住所状態」として扱い、住所修正導線に寄せる。
        setCompleted({
          forUid: user.uid,
          addressIsValid: sendingFee !== null,
        });
      } catch (_error) {
        if (cancelled) return;
        setCompleted({ forUid: user.uid, addressIsValid: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasAddressRequiredTicket, user]);

  if (!hasAddressRequiredTicket) return "idle";

  if (completed?.forUid !== user.uid) return "loading";

  if (!completed.addressIsValid) return "invalid";

  return "idle";
};

/**
 * class component 側から hook を使うための bridge component。
 *
 * 責務:
 * - hook の GuideStatus に応じた住所ガイド UI を組み立てる
 * - 住所が有効な時だけ従来の抽選下部 UI を描画する
 */
export const EventTicketAddressEditGuideGate: React.FC<
  EventTicketAddressEditGuideGateProps
> = ({ lotteryCart, tickets, user, contentWhenAddressValid }) => {
  const status = useEventTicketAddressEditGuide({
    lotteryCart,
    tickets,
    user,
  });

  if (status === "loading") {
    return <EventTicketAddressEditGuideLoading />;
  }

  if (status === "invalid") {
    return <EventTicketAddressEditGuideInvalid />;
  }

  return <>{contentWhenAddressValid}</>;
};
